import type {
	ProntuarioPresenceStatus,
	ProntuarioSessionFieldKind,
} from '../constants/prontuarioSessionChoices';
import { isProntuarioSessionChoiceValue } from '../constants/prontuarioSessionChoices';

export type ProntuarioPastSessionTask = {
	id: string;
	description: string;
	doneLabel: string;
	hadDifficultyLabel?: string;
	enjoyedLabel?: string;
	engagementRating?: number;
	reviewNotes?: string;
};

export type ProntuarioPastSessionRecord = {
	id: string;
	data?: string;
	resumo?: string;
	resumoTag?: string;
	resumoCurto?: string;
	resumoCurtoTag?: string;
	humorObservado?: string;
	humorObservadoTag?: string;
	queixasSecundarias?: string;
	queixasSecundariasTag?: string;
	conteudoTecnicas?: string;
	conteudoTecnicasTag?: string;
	evolucaoObservacoes?: string;
	evolucaoObservacoesTag?: string;
	indicadorRisco?: string;
	observacoesRisco?: string;
	observacoesRiscoTag?: string;
	sessaoNumero?: string;
	presenca?: ProntuarioPresenceStatus;
	tasks?: ProntuarioPastSessionTask[];
};

export type ProntuarioPastSessionFieldKey = Exclude<
	keyof ProntuarioPastSessionRecord,
	'id' | 'resumo' | 'tasks'
>;

export type ProntuarioPastSessionFieldDefinition = {
	key: ProntuarioPastSessionFieldKey;
	label: string;
	fullWidth?: boolean;
	kind: ProntuarioSessionFieldKind;
};

const parseSessaoNumero = (value?: string | null): number | null => {
	if (!value?.trim()) return null;
	const parsed = Number.parseInt(value.trim(), 10);
	return Number.isNaN(parsed) ? null : parsed;
};

export const isProntuarioFirstSession = (
	record: Pick<ProntuarioPastSessionRecord, 'sessaoNumero'>,
): boolean => parseSessaoNumero(record.sessaoNumero) === 1;

export const PRONTUARIO_PAST_SESSION_FIELDS: ReadonlyArray<ProntuarioPastSessionFieldDefinition> =
	[
		{ key: 'data', label: 'Data', fullWidth: true, kind: 'text' },
		{
			key: 'humorObservado',
			label: 'Humor observado',
			fullWidth: true,
			kind: 'text',
		},
		{
			key: 'queixasSecundarias',
			label: 'Queixas secundárias do dia',
			fullWidth: true,
			kind: 'text',
		},
		{
			key: 'conteudoTecnicas',
			label: 'Conteúdo / técnicas',
			fullWidth: true,
			kind: 'text',
		},
		{
			key: 'evolucaoObservacoes',
			label: 'Evolução / observações',
			fullWidth: true,
			kind: 'text',
		},
		{ key: 'indicadorRisco', label: 'Indicador de risco', kind: 'text' },
		{
			key: 'observacoesRisco',
			label: 'Observações de risco',
			fullWidth: true,
			kind: 'text',
		},
		{ key: 'sessaoNumero', label: 'Sessão nº', kind: 'text' },
		{ key: 'presenca', label: 'Presença', kind: 'presence' },
	];

const hasVisibleFieldValue = (
	record: ProntuarioPastSessionRecord,
	field: ProntuarioPastSessionFieldDefinition,
): boolean => {
	const value = record[field.key];
	if (value == null || value === '') return false;

	if (field.kind === 'text') {
		return typeof value === 'string' && value.trim().length > 0;
	}

	return (
		typeof value === 'string' &&
		isProntuarioSessionChoiceValue(value, field.kind)
	);
};

export const getProntuarioPastSessionVisibleFields = (
	record: ProntuarioPastSessionRecord,
) =>
	PRONTUARIO_PAST_SESSION_FIELDS.filter((field) =>
		hasVisibleFieldValue(record, field),
	);
